import { Card } from '../types';
import { getHandValue } from './deck';

/**
 * Basic strategy decision maker for AI players
 * Returns 'hit' or 'stand' based on basic blackjack strategy
 */
export function getBasicStrategyDecision(
    playerCards: Card[],
    dealerUpCard: Card
): 'hit' | 'stand' | 'double' | 'surrender' | 'split' {
    const { value, soft } = getHandValue(playerCards);
    const dealerValue = getDealerUpCardValue(dealerUpCard);

    // Pair Splitting
    if (playerCards.length === 2 && getCardValue(playerCards[0].rank) === getCardValue(playerCards[1].rank)) {
        const rank = playerCards[0].rank;

        // Aces and 8s: Always split
        if (rank === 'A' || rank === '8') return 'split';

        // 2s and 3s: Split vs 2-7
        if (['2', '3'].includes(rank)) {
            return (dealerValue >= 2 && dealerValue <= 7) ? 'split' : 'hit';
        }

        // 4s: Split vs 5-6 (some charts say only if DAS, assuming DAS for now or just simplified)
        if (rank === '4') {
            return (dealerValue === 5 || dealerValue === 6) ? 'split' : 'hit';
        }

        // 5s: Never split (treat as 10 and double vs 2-9)
        if (rank === '5') {
            // Treat as hard 10
            return (dealerValue >= 2 && dealerValue <= 9) ? 'double' : 'hit';
        }

        // 6s: Split vs 2-6
        if (rank === '6') {
            return (dealerValue >= 2 && dealerValue <= 6) ? 'split' : 'hit';
        }

        // 7s: Split vs 2-7
        if (rank === '7') {
            return (dealerValue >= 2 && dealerValue <= 7) ? 'split' : 'hit';
        }

        // 9s: Split vs 2-6, 8-9 (Stand vs 7, 10, A)
        if (rank === '9') {
            if (dealerValue === 7 || dealerValue === 10 || dealerValue === 11) return 'stand';
            return 'split';
        }

        // 10s: Never split
        if (['10', 'J', 'Q', 'K'].includes(rank)) return 'stand';
    }

    // Late surrender (first two cards only, hard totals, not a soft hand).
    // Placed after pair-splitting so 8-8 still splits rather than surrendering.
    const canSurrender = playerCards.length === 2 && !soft;
    if (canSurrender) {
        if (value === 16 && (dealerValue === 9 || dealerValue === 10 || dealerValue === 11)) return 'surrender';
        if (value === 15 && dealerValue === 10) return 'surrender';
    }

    // Soft hands (with Ace counted as 11)
    if (soft) {
        if (value >= 19) return 'stand';
        if (value === 18) {
            return dealerValue >= 9 ? 'hit' : 'stand';
        }
        return 'hit'; // Soft 17 or less, always hit
    }

    // Hard hands
    if (value >= 17) return 'stand';
    if (value <= 11) return 'hit';

    // 12-16 (hard)
    if (value >= 12 && value <= 16) {
        if (dealerValue >= 2 && dealerValue <= 6) {
            return 'stand'; // Dealer bust cards
        }
        return 'hit';
    }

    return 'hit';
}

function getCardValue(rank: string): number {
    if (rank === 'A') return 11;
    if (['J', 'Q', 'K'].includes(rank)) return 10;
    return parseInt(rank);
}

function getDealerUpCardValue(card: Card): number {
    if (card.rank === 'A') return 11;
    if (['J', 'Q', 'K'].includes(card.rank)) return 10;
    return parseInt(card.rank);
}
